/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { LayoutGridIcon } from "lucide-react"
import { observer } from "mobx-react-lite"
import { Link, useParams } from "react-router"

import { ApkDetailViewModel } from "../../features/apk-detail/apk-detail-viewmodel"
import { AppRoutes } from "../../routes/routes"
import { useViewModelWithArgs } from "../app/app-hooks"
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator
} from "../primitives/breadcrumb"
import { ApkCategories } from "./apk-categories"
import { ApkDetailInfo } from "./apk-detail-info"
import styles from "./apk-detail-view.module.css"
import { ApkExternalLinks } from "./apk-external-links"
import { ApkPermissions } from "./apk-permissions"
import { ApkThreatLevel } from "./apk-threat-level"

/**
 * Displays the Package Detail page for an APK opened from a device's malware tab.
 *
 * The APK is shared across devices, so the page is not tied to a specific device
 * and its breadcrumb links back to the dashboard.
 *
 * The route spinner replaces the page while the detail loads. If the first
 * request fails, only the header remains visible and the error appears as a toast.
 */
export const ApkDetailView = observer(() => {
    const { id } = useParams<{ id: string }>()

    // Keep the view-model key consistent with the numeric ID used by the route loader.
    const apk = useViewModelWithArgs(ApkDetailViewModel, Number(id)).apkDetail

    return (
        <div className="w-content max-inline-7xl">
            <header className="mb-6 grid gap-2">
                <h1 className="m-0 text-3xl font-bold text-primary">Package Detail</h1>
                <Breadcrumb>
                    <BreadcrumbList className="m-0 p-0 sm:gap-1.5">
                        <BreadcrumbItem>
                            <BreadcrumbLink render={<Link to={AppRoutes.Dashboard} aria-label="Dashboard" />}>
                                <LayoutGridIcon className="size-4" />
                            </BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                            <BreadcrumbPage>Package Detail</BreadcrumbPage>
                        </BreadcrumbItem>
                    </BreadcrumbList>
                </Breadcrumb>
            </header>

            {apk && (
                // Keep the right column occupied on narrow screens so its first card
                // does not move above the first card in the left column.
                <div className={styles.layout}>
                    <div className={styles.column}>
                        <ApkDetailInfo apk={apk} />
                        <ApkCategories apk={apk} />
                        <ApkPermissions permissions={apk.permissions} />
                    </div>
                    <div className={styles.column}>
                        <ApkThreatLevel apk={apk} />
                        <ApkExternalLinks apk={apk} />
                    </div>
                </div>
            )}
        </div>
    )
})
